<template>
  <div>
    <CRow>
      <CCol lg="12">
        <h1 class="main-header mb-1" v-translate translate-context="accounting.movements">
          Transactions
        </h1>
        <p class="text-help-dark" v-translate translate-context="accounting.movements">Register of accounting movements</p>

        <CCard>
          <CCardHeader class="border-0 ml-3">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipShoppingBag01" size="custom-size" :width="18" />
              <span class="mx-1" v-translate translate-context="accounting.movements">All transactions</span>
              <span>({{ movements.pagination.total|number }})</span>
            </div>

            <div class="card-header-actions">
              <CLink @click="onExport"
                     class="font-weight-medium d-flex align-items-center mr-3"
                     v-if="canExport">
                  <CIcon width="16" size="custom-size" name="cipDownload03" class="mr-2"/>
                  <translate translate-context="accounting">Download the detailed report (.xls)</translate>
              </CLink>
            </div>
          </CCardHeader>

          <CCardBody class="px-0">
            <div class="order-list-totals d-flex mb-4 mx-4" v-if="showTotals">
              <div v-if="movementsTotals.total_line_amount != null">
                <div class="d-flex align-items-center">
                  <CIcon name="cipCreditCard02" size="custom-size" width="40" height="40"
                         class="mr-3 icon-circle icon-circle--primary"/>
                  <div>
                    <div class="label">
                      <translate translate-context="accounting.movements">Total amount</translate>
                    </div>
                    <div class="value">{{ movementsTotals.total_line_amount|currency(movementsTotals.currency) }}</div>
                  </div>
                </div>
              </div>
              <div v-if="movementsTotals.net_photo_service != null">
                <div class="label">
                  <translate translate-context="accounting.movements">Photographer revenue</translate>
                </div>
                <div class="value">{{ movementsTotals.net_photo_service|currency(movementsTotals.currency) }}</div>
              </div>
            </div>

            <AjaxTable
                v-if="isReady"
                ref="movementsTable"
                :fields="fields"
                :data="movements"
                :fetch="fetchList"
                table-name="accounting-movements"
                :context-key="contextKey"
                :sync-url="true"
                class="custom-table custom-table--clean custom-table--has-calendar mx-4"
            >
              <template #effective_date="{item}">
                <td>
                  {{ item.effective_date|formatDateTime('L') }}
                </td>
              </template>

              <template #transaction_type="{item}">
                <td>
                  <CBadge class="badge-outline" :color="getTypeColor(item.transaction_type)">
                    <div class="d-flex align-items-center">
                      <CIcon :name="getTypeIcon(item.transaction_type)" size="custom-size" :width="14" class="mr-2" />
                      <span class="font-weight-normal">{{ getTypeLabel(item.transaction_type) }}</span>
                    </div>
                  </CBadge>
                </td>
              </template>

              <template #event_name="{item}">
                <td>
                  <template v-if="item.pica_services && item.pica_services.length">
                    <template v-for="(ps, idx) in item.pica_services">
                      <router-link
                        :key="ps.id"
                        :to="{ name: 'picaServiceRedirect', params: { picaServiceId: ps.id } }"
                        class="reference-link"
                        @click.native.stop
                      >{{ ps.event_name }}</router-link><span v-if="idx < item.pica_services.length - 1" :key="'sep-ps-' + idx">, </span>
                    </template>
                  </template>
                  <span v-else-if="item.event_name">{{ item.event_name }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>


              <template #reference="{item}">
                <td>
                 <CLink v-if="hasDetailPerm" @click="navigateToSource(item)" class="link--order">
                  <span v-if="item.order_number">
                    {{ item.order_number }}
                  </span>
                  <span v-else class="text-muted">
                    #{{ item.source_id }}
                  </span>
                 </CLink>
                 <template v-else>
                  <span v-if="item.order_number">
                    {{ item.order_number }}
                  </span>
                  <span v-else class="text-muted">
                    #{{ item.source_id }}
                  </span>
                 </template>
                </td>
              </template>

              <template #user_email="{item}">
                <td>
                  <span v-if="item.user_email">{{ item.user_email }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #currency="{item}">
                <td>
                  <span>{{ item.currency || '-' }}</span>
                </td>
              </template>

              <template #gross="{item}">
                <td>
                  <span v-if="item.gross != null">{{ item.gross|currency(item.currency) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #vat="{item}">
                <td>
                  <span v-if="item.vat != null">{{ item.vat|currency(item.currency) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #net_revenue="{item}">
                <td>
                  <span v-if="item.net_revenue != null">{{ item.net_revenue|currency(item.currency) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #net_pica="{item}">
                <td>
                  <span v-if="item.net_pica != null">{{ item.net_pica|currency(item.currency) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #net_photo_service="{item}">
                <td>
                  <span v-if="item.net_photo_service != null">{{ item.net_photo_service|currency(item.currency) }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #refund_type="{item}">
                <td>
                  <CBadge v-if="item.refund_type" color="primary" class="badge-outline">
                    {{ item.refund_type.name || item.refund_type }}
                  </CBadge>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #reason="{item}">
                <td>
                  <div v-if="item.reason" class="text-truncate" style="max-width: 180px;" v-c-tooltip="item.reason">
                    {{ item.reason }}
                  </div>
                  <span v-else class="text-muted">-</span>
                </td>
              </template>

              <template #actions="{item}">
                <td class="sticky-column">
                   <CButton color="dark"
                           variant="outline"
                           class="btn-circle btn-circle--sm d-inline-flex align-items-center justify-content-center"
                           size="sm"
                           @click="navigateToSource(item)">
                    <CIcon name="cipArrowRight"/>
                  </CButton>
                </td>
              </template>
            </AjaxTable>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PButton from '@/components/PButton.vue'
import AjaxTable from '@/domain/core/components/AjaxTable.vue'
import {
  MOVEMENT_TYPE_LABELS,
  MOVEMENT_TYPE_COLORS,
  MOVEMENT_TYPE_ICON,
  MOVEMENT_TYPES,
  getMovementDetailRouteParams
} from '../constants'
import {timeZone} from "@/utils/date";
import ConfigScope from "@/domain/core/constant/configScope";

export default {
  name: 'MovementList',
  components: {
    PButton,
    AjaxTable
  },
  props: {
    scopeType: {
      type: String,
      required: true,
      validator: v => ['global', 'contract-manager', 'pica-service'].includes(v)
    },
    scopeId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isReady: false,
      exporting: false
    }
  },
  async mounted() {
    await this.setScope(this.scope)

    await this.fetchScopedConfig()

    this.isReady = true
  },
  computed: {
    timeZone() {
      return timeZone
    },
    ...mapGetters('accounting', ['movements', 'movementsTotals']),

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    contextKey() {
      return `${this.scopeType}-${this.scopeId}`
    },

    hasDetailPerm() {
      return this.hasScopePerm('accounting.movements.detail')
    },

    isPicaServiceScope() {
      return this.scopeType === 'pica-service'
    },

    canExport() {
      return this.hasScopePerm('accounting.movements.export')
        && !this.exporting
        && this.movements.results
        && this.movements.results.length > 0
    },

    showTotals() {
      return this.isPicaServiceScope && this.movementsTotals &&
        (this.movementsTotals.total_line_amount != null || this.movementsTotals.net_photo_service != null)
    },

    movementTypeOptions() {
      const options = [
        { value: '', label: this.$pgettext('accounting.movements', 'All') },
      ]
      if (this.hasScopePerm('accounting.movements.types.orders')) {
        options.push({ value: MOVEMENT_TYPES.ORDER_ITEM, label: MOVEMENT_TYPE_LABELS[MOVEMENT_TYPES.ORDER_ITEM] })
      }
      if (this.hasScopePerm('accounting.movements.types.refunds')) {
        options.push({ value: MOVEMENT_TYPES.REFUND, label: MOVEMENT_TYPE_LABELS[MOVEMENT_TYPES.REFUND] })
      }
      return options
    },

    fields() {
      const fields = [
        {
          key: 'effective_date',
          label: this.$pgettext('accounting.movements', 'Date'),
          _style: 'min-width: 180px;',
          sorter: true,
          dateRangeFrom: 'date_from',
          dateRangeTo: 'date_to'
        },
      ]

      if (this.hasScopePerm('accounting.movements.fields.transaction_type')) {
        fields.push({
          key: 'transaction_type',
          label: this.$pgettext('accounting.movements', 'Type'),
          _style: 'width: 140px;',
          sorter: false,
          options: this.movementTypeOptions
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.order_number')) {
        fields.push({
          key: 'reference',
          label: this.$pgettext('accounting.movements', 'Reference'),
          _style: 'width: 170px;',
          sorter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.event_name')) {
        fields.push({
          key: 'event_name',
          label: this.$pgettext('accounting.movements', 'Event'),
          sorter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.user_email')) {
        fields.push({
          key: 'user_email',
          label: this.$pgettext('accounting.movements', 'User'),
          _style: 'width: 200px;',
          sorter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.gross')) {
        fields.push({
          key: 'gross',
          label: this.$pgettext('accounting.movements', 'Amount'),
          _style: 'width: 130px;',
          sorter: false,
          filter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.currency')) {
        fields.push({
          key: 'currency',
          label: this.$pgettext('accounting.movements', 'Currency'),
          _style: 'width: 90px;',
          sorter: false,
          filter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.vat')) {
        fields.push({
          key: 'vat',
          label: this.$pgettext('accounting.movements', 'VAT'),
          _style: 'width: 100px;',
          sorter: false,
          filter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.net_revenue')) {
        fields.push({
          key: 'net_revenue',
          label: this.$pgettext('accounting.movements', 'Net Revenue'),
          _style: 'width: 120px;',
          sorter: false,
          filter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.net_pica')) {
        fields.push({
          key: 'net_pica',
          label: this.$pgettext('accounting.movements', 'Net Pica'),
          _style: 'width: 120px;',
          sorter: false,
          filter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.net_photo_service')) {
        fields.push({
          key: 'net_photo_service',
          label: this.$pgettext('accounting.movements', 'Net Photo Service'),
          _style: 'width: 140px;',
          sorter: false,
          filter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.refund_type')) {
        fields.push({
          key: 'refund_type',
          label: this.$pgettext('accounting.movements', 'Refund Type'),
          _style: 'width: 140px;',
          sorter: false,
          filter: false
        })
      }

      if (this.hasScopePerm('accounting.movements.fields.reason')) {
        fields.push({
          key: 'reason',
          label: this.$pgettext('accounting.movements', 'Reason'),
          _style: 'width: 180px;',
          sorter: false,
          filter: false
        })
      }


      if (this.hasDetailPerm) {
        fields.push({
          key: 'actions',
          label: '',
          _style: 'width: 80px;',
          sorter: false,
          filter: false
        })
      }

      return fields
    }
  },
  methods: {
    ...mapActions('accounting', [
      'setScope',
      'fetchMovementList',
      'fetchMovementsTotals',
      'exportMovements'
    ]),
    ...mapActions('login', ['fetchConfig']),

    async fetchScopedConfig() {
      try {
        if (this.scopeType === 'pica-service') {
          await this.fetchConfig({
            type: 'accounting_pica_service',
            id: this.scopeId,
            scope: ConfigScope.PICA_SERVICE
          })
        } else if (this.scopeType === 'contract-manager') {
          await this.fetchConfig({
            type: 'accounting_contract_manager',
            id: this.scopeId
          })
        } else if (this.scopeType === 'global') {
          await this.fetchConfig({
            type: 'accounting_global'
          })
        }
      } catch (e) {
        // Config endpoint may not exist yet, continue without it
      }
    },

    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else if (this.scope.type === 'global') {
        fullPermission = `admin.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    async fetchList({ filters, pagination, sorting }) {
      const { transaction_type, ...restFilters } = filters
      const mappedFilters = transaction_type
        ? { ...restFilters, source_type: transaction_type }
        : restFilters

      const promises = [
        this.fetchMovementList({
          filters: mappedFilters,
          pagination,
          sorting
        })
      ]

      if (this.isPicaServiceScope) {
        promises.push(this.fetchMovementsTotals(mappedFilters))
      }

      await Promise.all(promises)
    },

    async onExport() {
      this.exporting = true
      try {
        const filters = this.$refs.movementsTable
          ? this.$refs.movementsTable.getFilters()
          : {}

        if (filters.transaction_type) {
          filters.source_type = filters.transaction_type
          delete filters.transaction_type
        }

        if (!filters.date_from) {
          const oneYearAgo = new Date()
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1)
          filters.date_from = oneYearAgo.toISOString().split('T')[0]
        }
        if (!filters.date_to) {
          filters.date_to = new Date().toISOString().split('T')[0]
        }

        await this.exportMovements(filters)
      } finally {
        this.exporting = false
      }
    },

    navigateToSource(item) {
      if (!this.hasDetailPerm) return
      const route = getMovementDetailRouteParams(item.transaction_type, item.source_id, this.scope)
      if (route) {
        this.$router.push(route)
      }
    },

    getTypeLabel(type) {
      return MOVEMENT_TYPE_LABELS[type] || type
    },

    getTypeColor(type) {
      return MOVEMENT_TYPE_COLORS[type] || 'secondary'
    },

    getTypeIcon(type) {
      return MOVEMENT_TYPE_ICON[type] || 'cipInfo'
    }
  }
}
</script>

<style scoped>
.custom-table {
  td {
    white-space: nowrap;
  }
}
</style>
